<!-- TODO: pdf.js has a very flushed out but complex implementation of grab to pan through its cursor we can implement: https://github.com/search?q=repo%3Amozilla%2Fpdf.js%20grabtopan&type=code -->

<!-- **Only use vh to control this component's height on a Desktop implementation (This is not meant for mobile)-->
<template>
  <div class="file-viewer">
    <div
      v-if="fileType !== null"
      class="contract-image text-center"
      :style="contractImageHeaderStyle"
    >
      <div
        ref="pdf"
      />
    </div>
    <div
      class="d-flex justify-center contract-image-controls-container"
      :class="{'p-fixed ml-n10': minimize, 'p-abs': !minimize}"
    >
      <div
        class="contract-image-controls space-between d-flex"
        :style="btnStyle"
      >
        <v-btn
          v-for="btn in actionBtns"
          :key="btn.icon"
          icon
          @click="btn.action"
        >
          <v-icon :size="btnSize">
            {{ btn.icon }}
          </v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { jsPDF } from 'jspdf';

export default {
  name: 'FileViewer',
  components: {
  },
  props: {
    imageData: {
      type: [ArrayBuffer, String],
      default: null,
    },
    fileType: {
      type: String,
      default: null,
    },
    minimize: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the file preview is currently expanded (from Sinoa dialog)
     * When true, shows minimize icon instead of expand icon
     * @type {boolean}
     */
    isExpanded: {
      type: Boolean,
      default: false,
    },
    btnSize: {
      type: String,
      default: '24px',
    },
  },
  data() {
    return {
      zoomRange: { min: 1, max: 5 },
      zoomFactor: 1.5,
      zoomIncrement: 0.5,
      localImgData: null, // Used for locally parsed images > pdf.js
    };
  },
  computed: {
    contractImageHeaderStyle() {
      return `height: calc(100vh - ${this.$headerHeight}px - 100px);`;
    },

    isImageFileType() {
      if (!this.fileType) return false;
      return this.fileType.startsWith('image/');
    },

    isPdfFileType() {
      return this.fileType === 'application/pdf';
    },

    btnStyle() {
      return `padding-left: ${this.btnSize}; padding-right: ${this.btnSize}; gap: ${this.btnSize}; padding-top: calc(${this.btnSize} / 4); padding-bottom: calc(${this.btnSize} / 4);`;
    },

    actionBtns() {
      const showMinimizeIcon = this.minimize || this.isExpanded;
      return [{
        icon: 'mdi-magnify-plus-outline',
        action: this.zoomIn,
      }, {
        icon: 'mdi-magnify-minus-outline',
        action: this.zoomOut,
      }, {
        icon: showMinimizeIcon ? 'mdi-fullscreen-exit' : 'mdi-fullscreen',
        action: this.toggleFullScreen,
      }];
    },
    zoomedOutOfRange() {
      if (this.zoomFactor < this.zoomRange.min || this.zoomFactor > this.zoomRange.max) {
        return true;
      }
      return false;
    },
  },
  watch: {
    async imageData() {
      await this.renderData();
    },
  },

  mounted() {
    this.renderData();
  },
  methods: {
    ...mapMutations('contractScanner', ['setContractImageData', 'setSalesContractFileType']),

    toggleFullScreen() {
      this.$emit('toggle-full-screen');
    },
    zoomIn() {
      this.zoomFactor += this.zoomIncrement;
      if (this.zoomedOutOfRange) {
        this.zoomFactor -= this.zoomIncrement;
        return;
      }
      this.renderPdf();
    },

    zoomOut() {
      this.zoomFactor -= this.zoomIncrement;
      if (this.zoomedOutOfRange) {
        this.zoomFactor += this.zoomIncrement;
        return;
      }
      this.renderPdf();
    },

    async renderData() {
      if (!this.imageData) {
        return;
      }

      // Priority: check fileType first, then fallback to data type detection
      if (this.isPdfFileType) {
        await this.renderPdf();
      } else if (this.isImageFileType) {
        await this.renderImg();
      } else if (this.imageData instanceof ArrayBuffer && !this.fileType) {
        await this.renderPdf();
      } else if (this.fileType) {
        await this.renderImg();
      }
    },

    async renderPage(pdf, container, numPages) {
      // Render each page
      const outputScale = window.devicePixelRatio || 1;
      for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 1) {
        pdf.getPage(pageNumber).then((page) => {
          const scale = this.zoomFactor;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          // Prepare canvas using PDF page dimensions
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          // Append the canvas to the container element
          container.appendChild(canvas);
          const context = canvas.getContext('2d');
          const renderContext = {
            canvasContext: context,
            viewport,
          };
          page.render(renderContext);
        });
      }
    },

    async renderPdf() {
      const pdfData = this.localImgData ? this.localImgData : this.imageData;
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.5.207/pdf.worker.min.js';
      const loadingTask = window.pdfjsLib.getDocument({ data: pdfData });
      const container = this.$refs.pdf;

      loadingTask.promise.then(async (pdf) => {
        const { numPages } = pdf; // Get the total number of pages in the PDF
        container.innerHTML = ''; // Clear previous pages
        // Render each page
        await this.renderPage(pdf, container, numPages);
      });
    },

    arrayBufferToDataUrl(buffer, mimeType) {
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = window.btoa(binary);
      return `data:${mimeType};base64,${base64}`;
    },

    loadImage(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = dataUrl;
      });
    },

    async renderImg() {
      if (!this.imageData) return;

      try {
        let dataUrl;
        const mimeType = this.fileType || 'image/jpeg';

        if (this.imageData instanceof ArrayBuffer) {
          dataUrl = this.arrayBufferToDataUrl(this.imageData, mimeType);
        } else if (typeof this.imageData === 'string') {
          dataUrl = this.imageData.startsWith('data:')
            ? this.imageData
            : `data:${mimeType};base64,${this.imageData}`;
        } else {
          return;
        }

        const img = await this.loadImage(dataUrl);
        const imgWidth = img.naturalWidth || img.width || 100;
        const imgHeight = img.naturalHeight || img.height || 100;

        const maxPdfWidth = 190; // A4 width with margins in mm
        const maxPdfHeight = 277; // A4 height with margins in mm
        const aspectRatio = imgWidth / imgHeight;

        let pdfWidth = maxPdfWidth;
        let pdfHeight = pdfWidth / aspectRatio;

        if (pdfHeight > maxPdfHeight) {
          pdfHeight = maxPdfHeight;
          pdfWidth = pdfHeight * aspectRatio;
        }

        // eslint-disable-next-line new-cap
        const doc = new jsPDF();
        const xOffset = (210 - pdfWidth) / 2; // A4 width is 210mm
        const yOffset = 10; // Small top margin

        const format = mimeType.includes('png') ? 'PNG' : 'JPEG';
        doc.addImage(dataUrl, format, xOffset, yOffset, pdfWidth, pdfHeight);

        this.localImgData = doc.output('arraybuffer');
        await this.renderPdf();
      } catch (error) {
        this.$snackbar.error(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.contract-image {
  height:100%;
  width:100%;
  overflow: hidden;
  max-height: inherit;
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    background-color: #fafafa;
  }

  .contract-image-controls {
    background-color: #fafafa;
      border: 1px solid #f5f5f5;
      border-radius: 9999px;
      pointer-events: auto;
    &-container {
      bottom: 25px;
      width: 100%;
      pointer-events: none;
    }
  }

.file-viewer {
  padding-top: 0;
  position: relative;
  height: 90%;
}
</style>
